import { useEffect, useState } from 'react'
import type { ClockState } from '@/domain/clock'
import { elapsedMs } from '@/domain/clock'
import { now } from '@/store/sessionStore'

/**
 * Tiempo transcurrido del reloj, refrescado con el ritmo de pintado del
 * navegador pero notificado solo cuando cambia la décima de segundo: el
 * anillo se ve fluido sin re-renderizar sesenta veces por segundo.
 */
export function useElapsedMs(clock: ClockState): number {
  const [value, setValue] = useState(() => elapsedMs(clock, now()))

  useEffect(() => {
    setValue(elapsedMs(clock, now()))
    if (clock.status !== 'running') return

    let frame = 0
    let lastTenth = -1

    const loop = () => {
      const current = elapsedMs(clock, now())
      const tenth = Math.floor(current / 100)
      if (tenth !== lastTenth) {
        lastTenth = tenth
        setValue(current)
      }
      frame = requestAnimationFrame(loop)
    }

    frame = requestAnimationFrame(loop)

    // Respaldo por si el navegador congela las animaciones en segundo plano:
    // al volver, el valor se recalcula de inmediato.
    const resync = () => setValue(elapsedMs(clock, now()))
    document.addEventListener('visibilitychange', resync)

    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('visibilitychange', resync)
    }
  }, [clock])

  return value
}
